/**
 * Assign or clear a member's till PIN.
 *
 * The PIN is what names a cashier on the shift they open, so this is a
 * manager's tool, not a personal setting — a cashier who could rewrite it
 * could rewrite who the drawer belonged to. The backend enforces the same
 * thing (TEAM_EDIT on PUT /users/{id}/pos-pin); this dialog only renders for
 * people who have it.
 *
 * The current PIN is never shown, because it is never sent: the server stores
 * a bcrypt hash and reports a boolean. Losing one means setting a new one.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Loader2, KeyRound, Shuffle } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import api from '../../lib/api'

export default function PosPinDialog({
  user, open, onOpenChange, onSaved,
}: {
  user: { id: number; name: string; has_pos_pin?: boolean } | null
  open: boolean
  onOpenChange: (v: boolean) => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const [pin, setPin] = useState('')
  const [confirm, setConfirm] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) { setPin(''); setConfirm('') }
  }, [open, user?.id])

  if (!user) return null

  const digitsOnly = (v: string) => v.replace(/\D/g, '').slice(0, 5)

  /** Mirrors the server's rules so a generated PIN is never bounced back:
   *  5 digits, not a run or a repeat. crypto.getRandomValues rather than
   *  Math.random — this is a credential, however short. */
  function generate() {
    const trivial = new Set(['12345', '54321', '01234', '12300'])
    for (;;) {
      const buf = new Uint32Array(5)
      crypto.getRandomValues(buf)
      const candidate = Array.from(buf, n => String(n % 10)).join('')
      if (new Set(candidate).size === 1 || trivial.has(candidate)) continue
      setPin(candidate)
      setConfirm(candidate)
      return
    }
  }
  const mismatch = confirm.length > 0 && pin !== confirm
  const invalidLength = pin.length > 0 && pin.length !== 5

  async function save(next: string | null) {
    setSaving(true)
    try {
      await api.put(`/users/${user!.id}/pos-pin`, { pin: next })
      toast.success(next
        ? t('users.pinSet', 'Till PIN updated for {{name}}.', { name: user!.name })
        : t('users.pinCleared', 'Till access removed for {{name}}.', { name: user!.name }))
      onSaved()
      onOpenChange(false)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail
        ?? t('common.saveFailed', 'Could not update the PIN.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            {t('users.tillPin', 'Till PIN')}
          </DialogTitle>
          <DialogDescription>
            {t('users.tillPinHint',
              '{{name}} enters this PIN on the terminal to open the register directly. Shifts they open are recorded under their name.',
              { name: user.name })}
          </DialogDescription>
        </DialogHeader>

        <div className="-mx-1 flex-1 space-y-4 overflow-y-auto px-1 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="pin-new">{t('users.newPin', 'New PIN')}</Label>
            <Input
              id="pin-new"
              value={pin}
              onChange={e => setPin(digitsOnly(e.target.value))}
              inputMode="numeric"
              autoComplete="off"
              placeholder="•••••"
              className="font-mono tracking-[0.4em]"
            />
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                {t('users.pinRule', 'Exactly 5 digits. Avoid runs and repeats like 12345 or 00000.')}
              </p>
              {/* Members added by invite create themselves, so nobody hands
                  them a PIN on the way in — this makes assigning one a click
                  instead of inventing digits that pass the rules. */}
              <Button type="button" variant="ghost" size="sm"
                      className="h-7 shrink-0 gap-1.5 text-xs" onClick={generate}>
                <Shuffle className="h-3 w-3" />
                {t('users.generatePin', 'Generate')}
              </Button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pin-confirm">{t('users.confirmPin', 'Confirm PIN')}</Label>
            <Input
              id="pin-confirm"
              value={confirm}
              onChange={e => setConfirm(digitsOnly(e.target.value))}
              inputMode="numeric"
              autoComplete="off"
              placeholder="•••••"
              className="font-mono tracking-[0.4em]"
            />
            {/* Confirmed rather than revealed: nobody can read this value back
                afterwards, so a typo would lock the cashier out silently. */}
            {mismatch && (
              <p className="text-xs text-destructive">
                {t('users.pinMismatch', "Those don't match.")}
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {user.has_pos_pin ? (
            <Button
              variant="ghost"
              className="text-destructive hover:text-destructive"
              disabled={saving}
              onClick={() => void save(null)}
            >
              {t('users.removePin', 'Remove till access')}
            </Button>
          ) : <span />}

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={() => void save(pin)}
              disabled={saving || pin.length !== 5 || pin !== confirm || invalidLength}
              className="gap-2"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('common.save', 'Save')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
